/**
 * Google Places service for competitor enrichment
 */

import { apiRequest } from './api';

export interface GooglePlaceData {
  place_id: string;
  name: string;
  formatted_address: string;
  geometry: {
    location: {
      lat: number;
      lng: number;
    };
  };
  types: string[];
  business_status?: string;
  formatted_phone_number?: string;
  website?: string;
  rating?: number;
  user_ratings_total?: number;
  opening_hours?: {
    open_now: boolean;
    weekday_text: string[];
  };
  photos?: Array<{
    photo_reference: string;
  }>;
  price_level?: number;
}

export interface EnrichedCompetitorData {
  name: string;
  address: {
    formatted_address: string;
    google_place_id: string;
    location?: {
      type: 'Point';
      coordinates: [number, number];
    };
  };
  phone?: string;
  website?: string;
  rating?: number;
  business_types?: string[];
  business_status?: string;
  operating_hours?: {
    open_now: boolean;
    weekday_text: string[];
  };
  distance_miles?: number;
  google_data?: {
    user_ratings_total: number;
    price_level?: number;
    photos: string[];
    place_id: string;
  };
}

export interface EnrichCompetitorResponse {
  status: string;
  competitor_data: EnrichedCompetitorData;
  raw_google_data: GooglePlaceData;
}

/**
 * Fetch Google Place details for a given place ID
 */
export const getPlaceDetails = async (placeId: string, fields?: string): Promise<GooglePlaceData> => {
  try {
    const params = new URLSearchParams({ place_id: placeId });
    if (fields) {
      params.append('fields', fields);
    }

    const response = await apiRequest(`/external/google-maps/places/details?${params.toString()}`, 'GET') as any;

    if (response.status !== 'OK') {
      throw new Error(`Google Places API error: ${response.status}`);
    }

    return response.result;
  } catch (error) {
    console.error('Error fetching place details:', error);
    throw error;
  }
};

/**
 * Enrich competitor data from Google Places
 */
export const enrichCompetitorFromGoogle = async (
  placeId: string,
  projectCoordinates?: { lat: number; lng: number }
): Promise<EnrichedCompetitorData> => {
  try {
    const params = new URLSearchParams({ place_id: placeId });
    
    if (projectCoordinates) {
      params.append('project_lat', projectCoordinates.lat.toString());
      params.append('project_lng', projectCoordinates.lng.toString());
    }

    const response = await apiRequest(`/external/google-maps/places/enrich-competitor?${params.toString()}`, 'POST') as EnrichCompetitorResponse;

    if (response.status !== 'success') {
      throw new Error('Failed to enrich competitor data from Google');
    }

    return response.competitor_data;
  } catch (error) {
    console.error('Error enriching competitor from Google:', error);
    throw error;
  }
};

/**
 * Format business types for display
 */
export const formatBusinessTypes = (types: string[]): string[] => {
  const typeMap: Record<string, string> = {
    'amusement_park': 'Amusement Park',
    'bowling_alley': 'Bowling Alley',
    'restaurant': 'Restaurant',
    'food': 'Food & Dining',
    'establishment': 'Business',
    'point_of_interest': 'Point of Interest',
    'store': 'Store',
    'shopping_mall': 'Shopping Mall',
    'gym': 'Gym',
    'health': 'Health & Fitness',
    'entertainment': 'Entertainment',
    'night_club': 'Night Club',
    'bar': 'Bar',
    'movie_theater': 'Movie Theater',
    'tourist_attraction': 'Tourist Attraction',
    'park': 'Park',
    'spa': 'Spa',
    'beauty_salon': 'Beauty Salon',
    'lodging': 'Lodging',
    'hospital': 'Hospital',
    'pharmacy': 'Pharmacy',
    'school': 'School',
    'university': 'University',
    'library': 'Library',
    'church': 'Church',
    'cemetery': 'Cemetery',
    'post_office': 'Post Office',
    'bank': 'Bank',
    'atm': 'ATM',
    'insurance_agency': 'Insurance',
    'real_estate_agency': 'Real Estate',
    'lawyer': 'Legal Services',
    'accounting': 'Accounting',
    'dentist': 'Dentist',
    'doctor': 'Doctor',
    'veterinary_care': 'Veterinary',
    'car_dealer': 'Car Dealer',
    'car_rental': 'Car Rental',
    'car_repair': 'Auto Repair',
    'parking': 'Parking',
    'subway_station': 'Subway',
    'train_station': 'Train Station',
    'bus_station': 'Bus Station',
    'airport': 'Airport'
  };

  return types
    .map(type => typeMap[type] || type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()))
    .filter((type, index, arr) => arr.indexOf(type) === index) // Remove duplicates
    .slice(0, 3); // Limit to 3 most relevant types
};

/**
 * Get business type icon/emoji
 */
export const getBusinessTypeIcon = (types: string[]): string => {
  const iconMap: Record<string, string> = {
    'amusement_park': '🎢',
    'bowling_alley': '🎳',
    'restaurant': '🍽️',
    'food': '🍕',
    'bar': '🍺',
    'night_club': '🍸',
    'movie_theater': '🎬',
    'gym': '💪',
    'spa': '💆',
    'shopping_mall': '🛍️',
    'store': '🏪',
    'gas_station': '⛽',
    'hospital': '🏥',
    'school': '🏫',
    'bank': '🏦',
    'hotel': '🏨',
    'lodging': '🏨',
    'park': '🌳',
    'church': '⛪',
    'tourist_attraction': '🗺️'
  };

  for (const type of types) {
    if (iconMap[type]) {
      return iconMap[type];
    }
  }

  return '🏢'; // Default business icon
};

/**
 * Format rating for display
 */
export const formatRating = (rating?: number, totalRatings?: number): string => {
  if (!rating) return 'No rating';
  
  const stars = '⭐'.repeat(Math.round(rating));
  const ratingText = `${rating.toFixed(1)} ${stars}`;
  
  if (totalRatings) {
    return `${ratingText} (${totalRatings} reviews)`;
  }
  
  return ratingText;
};

/**
 * Format operating hours for display
 */
export const formatOperatingHours = (hours?: { open_now: boolean; weekday_text: string[] }): string => {
  if (!hours) return 'Hours not available';
  
  const status = hours.open_now ? '🟢 Open now' : '🔴 Closed now';
  
  if (hours.weekday_text && hours.weekday_text.length > 0) {
    const today = new Date().getDay();
    const todayIndex = today === 0 ? 6 : today - 1; // Convert Sunday=0 to Monday=0 format
    const todayHours = hours.weekday_text[todayIndex] || hours.weekday_text[0];
    return `${status} • ${todayHours}`;
  }
  
  return status;
};

/**
 * Generate Google Maps URL for a place
 */
export const getGoogleMapsUrl = (placeId: string): string => {
  return `https://www.google.com/maps/place/?q=place_id:${placeId}`;
};

/**
 * Search for nearby competitors using Google Places Nearby Search
 */
export interface CompetitorSuggestion {
  place_id: string;
  name: string;
  formatted_address: string;
  location: {
    lat: number;
    lng: number;
  };
  distance_miles: number;
  rating?: number;
  user_ratings_total?: number;
  business_types: string[];
  business_status?: string;
  price_level?: number;
  photos: string[];
  suggested_competition_type: 'Direct' | 'Indirect' | 'Potential';
  suggested_threat_rating: number;
}

export interface NearbySearchResponse {
  status: string;
  search_params: {
    center: { lat: number; lng: number };
    radius_miles: number;
    business_type?: string;
    keyword?: string;
  };
  suggestions: CompetitorSuggestion[];
  total_found: number;
}

export const searchNearbyCompetitors = async (
  lat: number,
  lng: number,
  radiusMiles: number = 5.0,
  businessType?: string,
  keyword?: string
): Promise<NearbySearchResponse> => {
  try {
    const params = new URLSearchParams({
      lat: lat.toString(),
      lng: lng.toString(),
      radius_miles: radiusMiles.toString()
    });
    
    if (businessType) {
      params.append('business_type', businessType);
    }
    
    if (keyword) {
      params.append('keyword', keyword);
    }

    const response = await apiRequest(`/external/google-maps/places/nearby-search?${params.toString()}`, 'GET') as NearbySearchResponse;

    if (response.status !== 'success') {
      throw new Error('Failed to search nearby competitors');
    }

    return response;
  } catch (error) {
    console.error('Error searching nearby competitors:', error);
    throw error;
  }
};
